import type { Promisable } from "type-fest";

import {
  ASSESSMENT_HISTORY_PAGE_LIMIT_DEFAULT,
  ASSESSMENT_HISTORY_PAGE_LIMIT_MAX,
  personStateOf,
  type Assessment,
  type AssessmentHistoryPage,
  type Person,
  type PersonInvolvement,
  type PersonState,
} from "@thalamos/haven-common";

import { apiHooks } from "../../api/index.js";
import { useGrowingLimit, type Growth } from "../../hooks/useGrowingLimit.js";

const HISTORY_GROWTH: Growth = {
  step: ASSESSMENT_HISTORY_PAGE_LIMIT_DEFAULT,
  max: ASSESSMENT_HISTORY_PAGE_LIMIT_MAX,
};

/** The most recently finished of a young person's assessments, as far as they have been read. */
export type AssessmentHistoryRead = {
  /** Undefined until a page has been read, which is not the same as empty. */
  page: AssessmentHistoryPage | undefined;
  /** Whether a page is being read, the first or a larger one. */
  loading: boolean;
  /** Whether no page could be read at all; a later read failing is `rereadFailed`. */
  failed: boolean;
  /** Whether reading it again failed, leaving the page read before in place. */
  rereadFailed: boolean;
  /** Absent once no larger page can be asked for. */
  onShowMore: (() => Promisable<void>) | undefined;
};

/**
 * Why a record cannot be shown. `not_found` is a 404, the server's answer both
 * for an id nobody holds and for a record this session may not see —
 * indistinguishable on purpose. Everything else is `unavailable`.
 */
export type PersonLoadFailure = "not_found" | "unavailable";

export const personLoadFailure = (
  status: number | undefined,
  errored: boolean,
): PersonLoadFailure | undefined => {
  if (status === 200) {
    return undefined;
  }

  if (status === 404) {
    return "not_found";
  }

  return errored || status !== undefined ? "unavailable" : undefined;
};

const failed = (error: unknown): boolean =>
  error !== undefined && error !== null;

/**
 * One young person's record, who is involved with them, and what has been
 * assessed about them, as the person's page reads them.
 *
 * Each is taken only from a 200: a 4xx body resolves onto `data`, and showing
 * one as the record would show an error message as a child's details.
 */
export const usePerson = (
  requestedId: string,
  wanted: {
    /** Whether the finished assessments are wanted; they are not read until they are. */
    history: boolean;
    /** Whether the guests on the case are wanted; they are not read until they are. */
    guests: boolean;
  },
) => {
  const [{ data, loading, error, response }, refetch] =
    apiHooks.person.byId(requestedId);
  const person: Person | undefined =
    response?.status === 200 ? data : undefined;

  // Fetched beside the record rather than behind a click: who can reach a child
  // is part of reading the record, not a report somebody has to go looking for.
  const [
    {
      data: involvementsData,
      error: involvementsError,
      response: involvementsResponse,
    },
    refetchInvolvements,
  ] = apiHooks.involvement.forPerson(requestedId);

  const [
    {
      data: assessments,
      error: assessmentsError,
      response: assessmentsResponse,
    },
    refetchCurrent,
  ] = apiHooks.assessment.forPerson(requestedId, person);

  const { limit: historyLimit, onShowMore: onShowMoreHistory } =
    useGrowingLimit(HISTORY_GROWTH, requestedId);
  const [
    {
      data: history,
      loading: historyLoading,
      error: historyError,
      response: historyResponse,
    },
    refetchHistory,
  ] = apiHooks.assessment.historyForPerson(
    requestedId,
    person,
    { limit: historyLimit },
    { manual: !wanted.history },
  );

  const [
    { data: guestsData, error: guestsError, response: guestsResponse },
    refetchGuests,
  ] = apiHooks.personGuest.onCase(requestedId, person, {
    manual: !wanted.guests,
  });

  // Both, because one write can move an assessment from one to the other. A
  // history not wanted is left alone: it is read afresh once it is.
  const refetchAssessments = () =>
    Promise.all([
      refetchCurrent(),
      wanted.history ? refetchHistory() : undefined,
    ]);

  const historyPage = historyResponse?.status === 200 ? history : undefined;

  const involvements: PersonInvolvement[] | undefined =
    involvementsResponse?.status === 200 ? involvementsData : undefined;

  // Unknown until both have loaded: a state worked out from involvements that
  // have not arrived would call every person unassigned.
  const state: PersonState | undefined =
    person === undefined || involvements === undefined
      ? undefined
      : personStateOf(person, involvements);

  const assessmentList: Assessment[] | undefined =
    assessmentsResponse?.status === 200 ? assessments?.assessments : undefined;

  return {
    person,
    loading,
    failure: personLoadFailure(response?.status, failed(error)),
    refetch,
    involvements,
    involvementsFailed:
      failed(involvementsError) && involvementsResponse?.status !== 200,
    involvementsSettled:
      involvementsResponse !== undefined || failed(involvementsError),
    refetchInvolvements,
    state,
    assessmentList,
    assessmentListFailed:
      personLoadFailure(
        assessmentsResponse?.status,
        failed(assessmentsError),
      ) !== undefined,
    assessmentHistory: {
      page: historyPage,
      loading: historyLoading,
      failed:
        personLoadFailure(historyResponse?.status, failed(historyError)) !==
        undefined,
      // A failed read keeps the last good response, so the status alone says
      // nothing went wrong.
      rereadFailed: historyPage !== undefined && failed(historyError),
      onShowMore: onShowMoreHistory,
    } satisfies AssessmentHistoryRead,
    refetchAssessments,
    guests: guestsResponse?.status === 200 ? guestsData?.guests : undefined,
    guestsFailed: failed(guestsError) && guestsResponse?.status !== 200,
    refetchGuests: () =>
      wanted.guests ? refetchGuests() : Promise.resolve(undefined),
  };
};
